import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "@/firebase/client";
import { collection, getDocs, query, orderBy, where, limit } from "firebase/firestore";
import { Product } from "@/lib/types";
import { generateProductSlug } from "@/lib/seo-utils";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

export function ProductShowcaseSection() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [shouldFetch, setShouldFetch] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // Defer Firestore fetch until section is near viewport (PageSpeed)
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !shouldFetch) {
          setShouldFetch(true);
        }
      },
      { rootMargin: "200px", threshold: 0.01 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [shouldFetch]);

  useEffect(() => {
    if (!shouldFetch) return;

    const fetchProducts = async () => {
      try {
        const productsRef = collection(db, "products");
        const q = query(
          productsRef,
          where("active", "==", true),
          orderBy("sortOrder", "asc"),
          limit(12)
        );
        const querySnapshot = await getDocs(q);

        const fetchedProducts: Product[] = [];
        querySnapshot.forEach(doc => {
          fetchedProducts.push({ id: doc.id, ...doc.data() } as Product);
        });

        setProducts(fetchedProducts);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching products:", error);
        setLoading(false);
      }
    };

    fetchProducts();
  }, [shouldFetch]);

  const handleProductClick = (product: Product) => {
    const slug = product.slug || generateProductSlug(product.name);
    navigate(`/products/${slug}`);
  };

  // Calculate price range for a product
  const getPriceDisplay = (product: Product): string => {
    const prices = product.variants.map(v => v.stripePrice);
    const lowestPrice = Math.min(...prices);
    const highestPrice = Math.max(...prices);
    const hasUniformPricing = lowestPrice === highestPrice;

    return hasUniformPricing ? `$${lowestPrice.toFixed(2)}` : `From $${lowestPrice.toFixed(2)}`;
  };

  if (loading) {
    return (
      <section
        ref={sectionRef}
        className="py-16 px-4 bg-muted/30"
        aria-busy="true"
        aria-label="Loading products"
      >
        <div className="container mx-auto max-w-7xl">
          {/* Header skeleton */}
          <div className="text-center mb-12 space-y-4">
            <Skeleton className="h-10 w-64 mx-auto" />
            <Skeleton className="h-5 w-96 max-w-full mx-auto" />
          </div>

          {/* Product grid skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <Card key={i} className="overflow-hidden">
                <Skeleton className="w-full aspect-square" />
                <CardHeader className="pb-3 space-y-3">
                  <div className="flex gap-1">
                    <Skeleton className="h-5 w-14 rounded-full" />
                    <Skeleton className="h-5 w-16 rounded-full" />
                  </div>
                  <Skeleton className="h-5 w-3/4" />
                  <div className="space-y-1.5">
                    <Skeleton className="h-3.5 w-full" />
                    <Skeleton className="h-3.5 w-2/3" />
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-6 w-20" />
                    <Skeleton className="h-5 w-5 rounded" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (products.length === 0) {
    return null; // Don't show section if no products
  }

  return (
    <section ref={sectionRef} className="py-16 px-4 bg-muted/30">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center mb-12 space-y-4">
          <h2 className="text-h2 sm:text-h1 md:text-display font-bold">Explore Our Products</h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
            Browse our full catalog of custom products. Each item can be personalized with your own
            AI-generated designs.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
          {products.map(product => (
            <ProductCard
              key={product.id}
              product={product}
              onClick={handleProductClick}
              priceDisplay={getPriceDisplay(product)}
            />
          ))}
        </div>

        {/* View All CTA */}
        <div className="text-center">
          <Button size="lg" onClick={() => navigate("/products")} className="px-8">
            View All Products
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </section>
  );
}
